'use client'

import { useMemo, useRef } from 'react'
import { useSmoothParallax } from '@/components/login/use-smooth-parallax'
import styles from '@/components/login/login-left-background-effects.module.css'
import { LoginFloatingLogo } from '@/components/login/login-floating-logo'

const PARTICLES = Array.from({ length: 28 }, (_, index) => ({
  id: index,
  left: `${8 + ((index * 37) % 84)}%`,
  top: `${6 + ((index * 53) % 88)}%`,
  size: 1 + (index % 3),
  duration: `${12 + (index % 9)}s`,
  delay: `${-(index * 0.65)}s`,
  driftX: `${-6 + (index % 13)}px`,
  driftY: `${-14 - (index % 8)}px`,
  opacityMin: 0.08 + (index % 5) * 0.02,
  opacityMax: 0.22 + (index % 4) * 0.06,
}))

const CUBE_FACES: Array<{ transform: string }> = [
  { transform: 'translateZ(56px)' },
  { transform: 'rotateY(180deg) translateZ(56px)' },
  { transform: 'rotateY(90deg) translateZ(56px)' },
  { transform: 'rotateY(-90deg) translateZ(56px)' },
  { transform: 'rotateX(90deg) translateZ(56px)' },
  { transform: 'rotateX(-90deg) translateZ(56px)' },
]

export function LoginLeftBackgroundEffects() {
  const containerRef = useRef<HTMLDivElement>(null)
  const layersRef = useSmoothParallax(containerRef)
  const particles = useMemo(() => PARTICLES, [])

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden pointer-events-none select-none z-0 ${styles.root}`}
      aria-hidden
    >
      <div ref={layersRef} className={`absolute inset-0 ${styles.root}`}>
        <div className={`absolute inset-0 ${styles.mesh}`} />

        <div className={`absolute -inset-[20%] ${styles.auroraA}`} />
        <div className={`absolute -inset-[15%] ${styles.auroraB}`} />
        <div className={`absolute -inset-[18%] ${styles.auroraC}`} />

        <div className={`absolute inset-0 ${styles.particles}`}>
          {particles.map((particle) => (
            <span
              key={particle.id}
              className={styles.particle}
              style={{
                left: particle.left,
                top: particle.top,
                width: particle.size,
                height: particle.size,
                ['--duration' as string]: particle.duration,
                ['--delay' as string]: particle.delay,
                ['--drift-x' as string]: particle.driftX,
                ['--drift-y' as string]: particle.driftY,
                ['--opacity-min' as string]: particle.opacityMin,
                ['--opacity-max' as string]: particle.opacityMax,
              }}
            />
          ))}
        </div>

        <div
          className={`absolute top-[12%] right-[8%] w-44 h-44 opacity-[0.35] ${styles.wireframeCube}`}
        >
          <div className={`relative w-full h-full ${styles.wireframeCubeInner}`}>
            {CUBE_FACES.map((face, index) => (
              <div
                key={index}
                className={styles.wireframeFace}
                style={{ transform: face.transform }}
              />
            ))}
          </div>
        </div>

        <LoginFloatingLogo />
      </div>
    </div>
  )
}

export function LoginTitleGlow() {
  return (
    <div
      className={`pointer-events-none absolute left-0 top-[8.75rem] w-full max-w-md h-40 -z-[1] ${styles.titleGlow}`}
      aria-hidden
    />
  )
}
